import { useState, useEffect, useCallback } from 'react';
import { apiService } from '@/shared/services/api';
import { backendErrorMessage, errorStatus, exceptionMessage } from '@/shared/utils/apiError';
import type { ApiResponse, GuideWithExercises } from '@/shared/types';
import type { SubmissionForm } from '@/code/types';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  refetch: () => Promise<void>;
  execute: () => Promise<T>;
}

export function useApi<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  dependencies: unknown[] = []
): UseApiReturn<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const execute = useCallback(async (): Promise<T> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiCall();
      if (response.success && response.data) {
        setState({
          data: response.data,
          loading: false,
          error: null,
        });
        return response.data;
      } else {
        const errorMessage = response.error?.message || 'Error desconocido';
        setState({
          data: null,
          loading: false,
          error: errorMessage,
        });
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        backendErrorMessage(error) || exceptionMessage(error) || 'Error de conexión';
      setState({
        data: null,
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  }, [apiCall]);

  const refetch = useCallback(async () => {
    await execute();
  }, [execute]);

  useEffect(() => {
    execute();
    // Las dependencias las decide quien llama al hook: ESLint no puede verificarlas
    // estáticamente y agregar `execute` refetchearía en cada render, porque cambia con
    // `apiCall`, que los llamadores pasan como función inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    ...state,
    refetch,
    execute,
  };
}

// Hook específico para submissions
export function useSubmission() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitSolution = async (formData: SubmissionForm) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.submitSolution(formData);
      if (response.success && response.data) {
        return response.data;
      } else {
        const errorMessage = response.error?.message || 'Error al enviar solución';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      let errorMessage = 'Error de conexión';

      if (errorStatus(error) === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
        // Limpiar tokens pero no redirigir automáticamente
        apiService.clearAuthTokens();
      } else {
        errorMessage = backendErrorMessage(error) || exceptionMessage(error) || errorMessage;
      }

      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    submitSolution,
    loading,
    error,
  };
}

// Hook específico para obtener ejercicios disponibles
export function useAvailableExercises() {
  return useApi<GuideWithExercises[]>(
    () => apiService.getAvailableExercises(),
    []
  );
}
